/**
 * Phosphor persistence.
 *
 * A ping-pong accumulator holding one number per channel per pixel: how hard
 * that grain of phosphor is currently glowing. Two things happen to it.
 *
 * **Excitation is a max, not a blend.** The beam drives the grain *to* a level;
 * it does not average with whatever the grain was already doing. A bright flash
 * therefore fully re-excites, and `max(decayed, current)` is the whole of it.
 * The composite downstream takes `max(scene, trail)` for the same reason, so
 * the two halves of the loop finally agree.
 *
 * **Decay is two mechanisms at once**, which is the difference between a smear
 * and an afterglow:
 *
 *   - *Monomolecular*, `exp(-dt/tau)`. One trapped carrier per site, a fixed
 *     fraction leaving per unit time. This is what ends the trail.
 *   - *Bimolecular*, `dI/dt = -k I^(3/2)`. An electron has to find a hole, so
 *     the rate collapses as the population thins. Bright dumps fast, dim hangs
 *     on. The closed form is linear in `1/sqrt(I)`, which makes it one
 *     `inversesqrt` and a multiply-add — no second accumulator, no extra pass.
 *
 * Together they give a blown-out core that falls out of white in a few frames
 * and then sits there as a dim ghost for most of a second, and they give it per
 * channel, so the ghost drifts green on the way down instead of fading grey.
 *
 * `uDt` is seconds, so the trail lasts the same wall-clock time at 60Hz and at
 * 144Hz, and the same number of frames under a harness that steps frames by
 * hand. Nothing in here is per-frame.
 */

import { GLSL_COLOR } from './common';

export const PERSISTENCE_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tCurrent;
uniform sampler2D tPrev;
uniform float uDt;
/*
 * exp(-dt/tau), computed once per frame on the CPU. It is a frame constant,
 * and the shader was evaluating three exponentials per pixel per frame at
 * region resolution to arrive at the same three numbers.
 */
uniform vec3 uMonoDecay;
uniform vec3 uRate;
uniform float uTrailSaturation;
uniform float uFloor;

in vec2 vUv;
out vec4 fragColor;

${GLSL_COLOR}

void main() {
  vec3 cur = max(texture(tCurrent, vUv).rgb, vec3(0.0));
  vec3 prev = max(texture(tPrev, vUv).rgb, vec3(0.0));

  prev *= uMonoDecay;

  // u = 1/sqrt(I) advances linearly at k/2, which is the exact solution of
  // dI/dt = -k I^(3/2) rather than a step of it. The clamp is only there to
  // keep an empty pixel from reaching for 1/sqrt(0); the floor below removes
  // the 1e-6 it leaves behind.
  vec3 u = inversesqrt(max(prev, vec3(1e-6))) + uRate * uDt * 0.5;
  prev = 1.0 / (u * u);

  // Push the hue out as the trail loses energy: white core, coloured wake.
  float l = luma(prev);
  prev = max(mix(vec3(l), prev, uTrailSaturation), vec3(0.0));

  // The bimolecular tail falls as 1/t² and would sit just above the half-float
  // denormals until the round ended. Cutting it is what keeps the black floor
  // black wherever anything bright has ever been.
  prev *= smoothstep(uFloor * 0.25, uFloor, luma(prev));

  fragColor = vec4(max(prev, cur), 1.0);
}
`;
